/* DC event page runtime. Loaded ONCE per page by the hero section (window.__dcEvent
   guard; the section's inline loader also guards the script tags). Owns:
     1. gsap.registerPlugin(ScrollTrigger) and the ScrollTrigger config.
     2. Lenis smooth scroll with the canonical glue from the skill file:
          lenis.on('scroll', ScrollTrigger.update)
          gsap.ticker.add((t) => lenis.raf(t * 1000))
          gsap.ticker.lagSmoothing(0)
        Lenis is skipped under prefers-reduced-motion or Save-Data (native scroll, the
        triggers still work). Store drawers scroll natively (Lenis prevent) and Lenis
        stops while the theme locks the body (overflow-hidden class).
     3. ScrollTrigger.refresh() on load, on document.fonts.ready and on debounced resize
        (height-only resizes on touch devices are ignored: the mobile URL bar).
     4. The page progress rail (fixed right, Mugen style; OFF since 2026-09-21) built from
        .dc-section[data-chapter]; the active chapter is tracked by a ScrollTrigger per
        chapter; clicking a step scrolls to it.
     5. Letting a phone scroll over a Spline canvas (see the guard below).
   API on window.__dcEvent: { gsap, ScrollTrigger, lenis, reduced, saveData, scrollTo,
   refresh, rail, swipedAt }. Dispatches 'dc:event:ready' on document once the API
   exists. */
(() => {
  if (window.__dcEvent) return;
  const gsap = window.gsap;
  const ST = window.ScrollTrigger;
  if (!gsap || !ST) return;
  gsap.registerPlugin(ST);

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches && !window.__dcForceMotion;
  const saveData = !!(navigator.connection && navigator.connection.saveData);
  const PREVENT = 'cart-drawer, menu-drawer, .drawer, .cart-drawer, predictive-search, [data-lenis-prevent]';

  ST.config({ ignoreMobileResize: true, autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load' });

  /* 2. Lenis */
  let lenis = null;
  if (typeof window.Lenis === 'function' && !reduced && !saveData) {
    try {
      lenis = new window.Lenis({
        lerp: 0.1,
        smoothWheel: true,
        prevent: (node) => !!(node && node.matches && node.matches(PREVENT)),
      });
      lenis.on('scroll', ST.update);
      gsap.ticker.add((t) => lenis.raf(t * 1000));
      gsap.ticker.lagSmoothing(0);
      /* Refresh (Dawn) locks the body with .overflow-hidden while a drawer is open. */
      if (window.MutationObserver) {
        const sync = () => {
          const locked = document.body.classList.contains('overflow-hidden');
          if (locked && !lenis.isStopped) lenis.stop();
          else if (!locked && lenis.isStopped) lenis.start();
        };
        new MutationObserver(sync).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      }
    } catch (e) {
      lenis = null;
    }
  }

  const scrollTo = (target, opts) => {
    let y = target;
    if (typeof target !== 'number') {
      const el = typeof target === 'string' ? document.querySelector(target) : target;
      if (!el) return;
      y = el.getBoundingClientRect().top + window.scrollY;
    }
    const o = opts || {};
    if (lenis) lenis.scrollTo(y, Object.assign({ duration: 1.2 }, o));
    else window.scrollTo({ top: y + (o.offset || 0), behavior: o.immediate ? 'auto' : 'smooth' });
  };
  /* The store header sits over the top of whatever is scrolled to. */
  const headerHeight = () => {
    let h = 0;
    document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => { h += el.getBoundingClientRect().height; });
    return Math.round(h);
  };

  /* 3. Refresh */
  const refresh = () => ST.refresh();
  let resizeTimer = 0;
  let lastW = window.innerWidth;
  window.addEventListener('resize', () => {
    const w = window.innerWidth;
    if (ST.isTouch === 1 && w === lastW) return;
    lastW = w;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(refresh, 200);
  }, { passive: true });
  if (document.readyState === 'complete') requestAnimationFrame(refresh);
  else window.addEventListener('load', () => requestAnimationFrame(refresh));
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => refresh()).catch(() => {});

  /* 4. Rail */
  const rail = { el: null, steps: [], triggers: [], chapters: [], active: -1 };
  const findChapters = () => Array.from(document.querySelectorAll('.dc-section[data-chapter], .dc-section > [data-chapter]'));
  const setActive = (i) => {
    if (rail.active === i) return;
    rail.active = i;
    rail.steps.forEach((s, k) => {
      s.classList.toggle('is-on', k === i);
      if (k === i) s.setAttribute('aria-current', 'true');
      else s.removeAttribute('aria-current');
    });
  };
  const destroyRail = () => {
    rail.triggers.forEach((t) => t.kill());
    rail.triggers = [];
    rail.steps = [];
    rail.chapters = [];
    rail.active = -1;
    if (rail.el) rail.el.remove();
    rail.el = null;
  };
  /* 2026-09-21 (JJ): the rail is off, it sat over the content on the right edge. The
     chapter lookup stays for the #anchor scrolling below. Flip RAIL to bring it back. */
  const RAIL = false;
  const buildRail = () => {
    destroyRail();
    if (!RAIL) return;
    const chapters = findChapters();
    if (!chapters.length) return;
    const nav = document.createElement('nav');
    nav.className = 'dc-rail';
    nav.setAttribute('aria-label', 'Page chapters');
    chapters.forEach((el, i) => {
      const name = (el.getAttribute('data-chapter') || '').trim() || String(i + 1).padStart(2, '0');
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'dc-rail__step';
      b.setAttribute('aria-label', 'Go to ' + name);
      const label = document.createElement('b');
      label.className = 'dc-rail__label';
      label.textContent = name;
      const line = document.createElement('i');
      line.className = 'dc-rail__line';
      line.setAttribute('aria-hidden', 'true');
      b.appendChild(label);
      b.appendChild(line);
      b.addEventListener('click', () => scrollTo(el));
      nav.appendChild(b);
      rail.steps.push(b);
    });
    document.body.appendChild(nav);
    rail.el = nav;
    rail.chapters = chapters;
    chapters.forEach((el, i) => {
      rail.triggers.push(ST.create({
        id: 'dc-rail:' + i,
        trigger: el,
        start: 'top 50%',
        end: 'bottom 50%',
        onToggle: (self) => { if (self.isActive) setActive(i); },
      }));
    });
    if (rail.active < 0) setActive(0);
  };

  /* 6. In-page links. The hero's GET TICKETS (and any other #anchor on the page) is
     scrolled to here rather than by the browser's own jump: the jump lands under the
     store header, and with a pinned hero above the target it is off by the pin's whole
     scroll span until the next refresh. A target is an element with that id, or a
     chapter whose name matches (so #tickets finds the TICKETS block even without an
     anchor). Clicks that are not plain left clicks are left to the browser. */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target && e.target.closest ? e.target.closest('a[href^="#"]') : null;
    if (!a) return;
    const id = a.getAttribute('href').slice(1);
    if (!id) return;
    let el = document.getElementById(id);
    if (!el) el = findChapters().find((c) => (c.getAttribute('data-chapter') || '').trim().toLowerCase() === id.toLowerCase()) || null;
    if (!el) return;
    e.preventDefault();
    scrollTo(el, { offset: -headerHeight() });
  });

  /* 5. Scrolling over a Spline canvas.

     A Spline runtime with interactive events bound cancels touchmove on its canvas so it
     can drag, and a cancelled touchmove is a phone that will not scroll. Measured on the
     tickets canvas, where the cards listen for taps: touchmove came back defaultPrevented,
     while the sponsors canvas, which binds no events, scrolled fine. So a thumb that
     landed on the ticket stage was stuck there until it found a gap between the stages.

     touch-action does not fix this. It is advisory, and a preventDefault from a
     non-passive listener beats it. The runtime has to not SEE the move: a capture
     listener on the document runs before any listener on the canvas whatever order they
     registered in, and stopPropagation there ends the trip. Lenis is unaffected because
     it runs with syncTouch off, so touchmove is a no-op for it and the phone is on native
     scroll anyway.

     Nothing in these scenes drags, so a moving touch is always a scroll. It is recorded
     rather than just blocked, because a scene that flips a card on tap must not flip one
     at the end of a swipe: dc-tickets checks swipedAt before it acts on a tap. */
  const SCENE = '[data-dc-canvas], [data-dc-bg]';
  const SWIPE_PX = 6;
  let swipedAt = 0;
  let touch = null;
  const onStart = (e) => {
    const t = e.touches && e.touches[0];
    const el = t && t.target && t.target.closest && t.target.closest(SCENE);
    touch = el ? { x: t.clientX, y: t.clientY } : null;
    /* Cleared per gesture, not on a timer. A reader asks "did THIS touch move", so a
       scroll must not go on swallowing the deliberate tap that follows it a moment later. */
    swipedAt = 0;
  };
  const onMove = (e) => {
    if (!touch) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    if (Math.abs(t.clientY - touch.y) < SWIPE_PX && Math.abs(t.clientX - touch.x) < SWIPE_PX) return;
    swipedAt = performance.now();
    e.stopPropagation();
  };
  const onEnd = () => { touch = null; };
  const CAP = { capture: true, passive: true };
  document.addEventListener('touchstart', onStart, CAP);
  document.addEventListener('touchmove', onMove, CAP);
  document.addEventListener('touchend', onEnd, CAP);
  document.addEventListener('touchcancel', onEnd, CAP);

  const api = {
    version: '1',
    gsap,
    ScrollTrigger: ST,
    lenis,
    reduced,
    saveData,
    scrollTo,
    refresh,
    /* performance.now() of the last swipe that started on a scene canvas, 0 for none. */
    get swipedAt() { return swipedAt; },
    rail: {
      rebuild: buildRail,
      setActive,
      get el() { return rail.el; },
      get steps() { return rail.steps; },
      get chapters() { return rail.chapters; },
      get active() { return rail.active; },
    },
  };
  window.__dcEvent = api;

  const onReady = (fn) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn, { once: true });
    else fn();
  };
  onReady(() => { buildRail(); refresh(); });

  if (window.Shopify && window.Shopify.designMode) {
    let editorTimer = 0;
    const rebuild = () => { clearTimeout(editorTimer); editorTimer = setTimeout(() => { buildRail(); refresh(); }, 60); };
    document.addEventListener('shopify:section:load', rebuild);
    document.addEventListener('shopify:section:unload', rebuild);
    document.addEventListener('shopify:section:reorder', rebuild);
  }

  document.dispatchEvent(new CustomEvent('dc:event:ready', { detail: api }));
})();
